import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  NatConfiguration,
  NatConfigurationPrivileges,
} from 'data/natConfiguration/natConfiguration.types';
import * as actions from 'data/natConfiguration/natConfiguration.actions';
import * as selectors from 'data/natConfiguration/natConfiguration.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as accessFunctionActions from 'data/accessFunction/accessFunction.actions';
import * as collectionFunctionGroupActions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as collectionFunctionActions from 'data/collectionFunction/collectionFunction.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import cloneDeep from 'lodash.clonedeep';
import NatConfigurationGrid from './components/NatConfigurationGridList';
import { distributorColumnDefs } from '../../NatConfiguration.types';

  type RouterParams = {
    history: string
  }

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getNatConfigurationList(state),
  selectedNatConfiguration: selectors.getSelectedNatConfiguration(state),
  userDBId: state.authentication.userDBId,
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  goToNatConfigurationCreate: actions.goToNatConfigurationCreate,
  goToNatConfigurationView: actions.goToNatConfigurationView,
  goToNatConfigurationEdit: actions.goToNatConfigurationEdit,
  selectNatConfiguration: actions.selectNatConfiguration,
  getNatConfigurationList: actions.getNatConfigurationList,
  deleteNatConfiguration: actions.deleteNatConfiguration,
  getAccessFunctions: accessFunctionActions.getAccessFunctionsWithViewOptions,
  getCollectionFunctionGroups: collectionFunctionGroupActions.getCollectionFunctionGroupList,
  fetchCollectionFunctionList: collectionFunctionActions.fetchCollectionFunctionList,
};

const connector = connect(mapState, mapDispatch);

  type PropsFromRedux = ConnectedProps<typeof connector>
  type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const NatConfigurationList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  selectedNatConfiguration,
  goToNatConfigurationCreate,
  goToNatConfigurationView,
  goToNatConfigurationEdit,
  selectNatConfiguration,
  getNatConfigurationList,
  deleteNatConfiguration,
  getAccessFunctions,
  getCollectionFunctionGroups,
  fetchCollectionFunctionList,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [dataGrid, setDataGrid] = useState<NatConfiguration[] | []>([]);
  const [columnDefs] = useState<ColDef[]>(distributorColumnDefs);

  useEffect(() => {
    getNatConfigurationList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getCollectionFunctionGroups(deliveryFunctionId);
    fetchCollectionFunctionList(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getNatConfigurationList,
    getAccessFunctions,
    getCollectionFunctionGroups,
    fetchCollectionFunctionList,
  ]);

  useEffect(() => {
    if (data) {
      const clonedData = cloneDeep(data);
      setDataGrid(clonedData);
    }
  }, [data]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getNatConfigurationList(deliveryFunctionId);

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToNatConfigurationCreate(history);
  };

  const handleEdit = () => goToNatConfigurationEdit(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectNatConfiguration(event.api.getSelectedRows());
  };

  const handleSelectItemMenuAction = () => {
    goToNatConfigurationView(history);
  };

  const handleDeleteRow = () => {
    const { id } = selectedNatConfiguration[0];

    const distributorConfigrationId = selectedNatConfiguration[0]?.id;
    if (distributorConfigrationId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
          selectedNatConfiguration[0].id as string,
          AuditService.NETWORK,
          AuditActionType.NETWORK_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteNatConfiguration(deliveryFunctionId, distributorConfigrationId, body, history, id);
  };

  const hasPrivilegeToView = privileges.includes(NatConfigurationPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(NatConfigurationPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(NatConfigurationPrivileges.Delete);

  return (
    <Fragment>
      <NatConfigurationGrid
        data={dataGrid}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onEdit={handleEdit}
        onSelectionChanged={handleSelectionChanged}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        lastUpdatedAt={lastUpdatedAt}
        onDeleteRow={handleDeleteRow}
        selectedNatConfiguration={selectedNatConfiguration[0]}
      />
    </Fragment>
  );
};

export default withRouter(connector(NatConfigurationList));
